<script setup lang="ts">
/**
 * WHAT HAS RUN OUT, marked from the pass — the kitchen knows first.
 *
 * One big row per dish of this station: a tap marks it out, another puts it
 * back. The tills stop offering it at once; it comes back by itself when the
 * trading day ends, because nobody remembers to turn the goulash back on.
 */
import type { Translator } from "@gf/kernel/i18n";

defineProps<{
  t: Translator;
  items: readonly { itemRef: string; name: string; soldOut: boolean }[];
}>();

const emit = defineEmits<{ toggle: [itemRef: string, soldOut: boolean]; close: [] }>();
</script>

<template>
  <!-- A tap beside the sheet closes it, as anywhere else on a touch screen. -->
  <div class="sheet__scrim" @click="emit('close')" />
  <div class="sheet" role="dialog" aria-modal="true" :aria-label="t.t('kds.soldOutTitle')">
    <header class="sheet__head">
      <h2 class="sheet__title">{{ t.t("kds.soldOutTitle") }}</h2>
      <button type="button" class="sheet__close" @click="emit('close')">{{ t.t("kds.close") }}</button>
    </header>
    <p class="sheet__lead">{{ t.t("kds.soldOutLead") }}</p>
    <ul class="sheet__list">
      <li v-for="item in items" :key="item.itemRef">
        <button
          type="button"
          class="sheet__item"
          :class="{ 'sheet__item--out': item.soldOut }"
          :data-item="item.itemRef"
          :aria-pressed="item.soldOut"
          @click="emit('toggle', item.itemRef, !item.soldOut)"
        >
          <span class="sheet__name">{{ item.name }}</span>
          <span class="sheet__state">{{ item.soldOut ? t.t("kds.soldOut") : t.t("kds.available") }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
/* It has left the page: the overlay tokens, the floating radius, the overlay
   shadow — and the stacking tokens, not a hand-picked 9 and 10. */
.sheet__scrim {
  position: fixed;
  inset: 0;
  z-index: var(--gf-z-overlay);
  background: var(--gf-color-scrim);
}

.sheet {
  position: fixed;
  inset: var(--gf-space-4) var(--gf-space-4) var(--gf-space-4) auto;
  z-index: var(--gf-z-modal);
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-3);
  width: min(32rem, calc(100vw - 2 * var(--gf-space-4)));
  padding: var(--gf-space-4);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-xl);
  background: var(--gf-color-surface-raised);
  box-shadow: var(--gf-shadow-lg);
  overflow-y: auto;
}

.sheet__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gf-space-3);
}

.sheet__title {
  margin: 0;
  font-size: var(--gf-text-xl);
  font-weight: var(--gf-weight-semibold);
  letter-spacing: var(--gf-tracking-tight);
}

.sheet__lead {
  margin: 0;
  color: var(--gf-color-text-muted);
}

.sheet__close {
  min-height: var(--gf-target-min);
  padding: 0 var(--gf-space-4);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-surface);
  color: var(--gf-color-text);
  font: inherit;
  font-weight: var(--gf-weight-semibold);
  box-shadow: var(--gf-shadow-sm);
  cursor: pointer;
}

.sheet__list {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-1);
  margin: 0;
  padding: 0;
  list-style: none;
}

.sheet__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gf-space-3);
  width: 100%;
  min-height: var(--gf-target-min);
  padding: var(--gf-space-2) var(--gf-space-3);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-surface);
  color: var(--gf-color-text);
  font: inherit;
  font-size: var(--gf-text-lg);
  text-align: left;
  cursor: pointer;
  transition: background-color var(--gf-motion-fast) var(--gf-ease-out);
}

.sheet__item:active {
  background: var(--gf-color-surface-active);
}

/* Out: a danger tint and its own ink — the word says it, the tint finds it. */
.sheet__item--out {
  background: var(--gf-color-danger-surface);
}

.sheet__name {
  font-weight: var(--gf-weight-semibold);
}

.sheet__state {
  flex-shrink: 0;
  white-space: nowrap;
  color: var(--gf-color-text-muted);
  font-size: var(--gf-text-sm);
  font-weight: var(--gf-weight-bold);
}

.sheet__item--out .sheet__state {
  color: var(--gf-color-danger);
}

.sheet__close:focus-visible,
.sheet__item:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}

@media (prefers-reduced-motion: reduce) {
  .sheet__item {
    transition: none;
  }
}
</style>
